import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Tooltip } from '@syncfusion/ej2-popups';

/**
 * Horizontal / vertical scale Syncfusion's `Tooltip` believes sits between its target's
 * screen rect and the coordinate space its popup is positioned in. Mirrors the shape of the
 * private `getScalingFactor` return value.
 */
interface TooltipScalingFactors {
  x: number;
  y: number;
}

/**
 * The subset of `Tooltip`'s private members the shim reads. They are not part of
 * Syncfusion's public typings, so the prototype is cast to this shape to wrap them. This
 * interface is the one place the private surface is named: verified against
 * `@syncfusion/ej2-popups` 34.1.29 (`getScalingFactor`, `isBodyContainer`, `tooltipEle`).
 * Re-check it on every Syncfusion upgrade; {@link SyncfusionTooltipZoomService.install}
 * validates the method before patching and warns instead of installing when it is gone.
 */
interface TooltipInternals {
  /** Scale Syncfusion believes sits between the target's screen rect and the popup's coordinate space. */
  getScalingFactor(target: HTMLElement | null | undefined): TooltipScalingFactors;
  /** True when the popup is appended to `<body>` (the default `container`), i.e. inside the zoomed body. */
  isBodyContainer?: boolean;
  /** The rendered popup element; absent until the tooltip has been created. */
  tooltipEle?: HTMLElement;
}

/**
 * Installs a one-time shim over Syncfusion's `Tooltip` positioning so every `ejs-tooltip`
 * lands next to its target under the CSS `zoom` the shell applies to `<body>` (see
 * {@link BodyZoomService}). Companion to {@link SyncfusionMenuZoomService}, which corrects
 * the menu pipelines the same way.
 *
 * Root cause: `Tooltip.getTooltipPosition` (ej2-popups) measures the target with
 * `getBoundingClientRect()` plus the document scroll — i.e. in *unzoomed* root pixels — and
 * the value is then applied as inline `top`/`left` on a popup appended to the zoomed `<body>`,
 * where it renders multiplied by the zoom. Under the app's 0.8 body zoom every tooltip
 * therefore opened up-left of its icon (the DocAI "beta" info tooltip on the reviewer /
 * performer edit pages sat over the Date/Status rows). Syncfusion's own zoom branch in that
 * method only handles a tooltip rendered *outside* the zoomed ancestor, so it never fires
 * for the default body container.
 *
 * Syncfusion does already carry the exact correction for a target inside a
 * `transform: scale(...)` ancestor: when `getScalingFactor` reports a scale,
 * `calculateTooltipOffset` measures the popup and its arrow via `getBoundingClientRect()`
 * (root pixels, consistent with the anchor), the collision / flip / fit checks run in root
 * pixels, and `getTooltipPosition` divides the final offset by the scale. A body zoom is the
 * same situation — a screen rect scaled relative to the popup's coordinate space — so this
 * shim reports the body zoom from `getScalingFactor` and lets that pipeline do the rest.
 * (Dividing only the finished offset would over-correct the popup's own size term, which is
 * measured in CSS pixels on the unscaled path, and left the arrow ~40px off the icon.)
 *
 * `renderPopup`, `reposition`, the window-resize handler and the mouse-trail handler all read
 * the scale through this one method, so a single wrap covers every path. No-op at zoom 1, for
 * a non-body container (the offset is then container-relative and not affected by the body
 * zoom), and for a tooltip whose computed position is `fixed` — the `.sfdv-pdf` rule in
 * `styles.css` already relocates the PDF viewer's toolbar tooltips that way, and a fixed
 * popup must keep the unzoomed values. A transform scale Syncfusion detects itself is
 * multiplied in rather than replaced. Remove once Syncfusion handles body zoom in tooltip
 * positioning natively.
 */
@Injectable({ providedIn: 'root' })
export class SyncfusionTooltipZoomService {

  /** Guard so the prototype patch is installed exactly once per page load — static so
   * even a second service instance (e.g. in tests) cannot double-wrap the prototype. */
  private static installed = false;

  /**
   * @param document - Injected document; also the platform-safe route to the window object
   * used for the computed-style reads inside the shim.
   */
  constructor(@Inject(DOCUMENT) private document: Document) {}

  /**
   * Installs the tooltip-positioning shim. Safe to call repeatedly — only the first call
   * that finds the expected Syncfusion method patches the prototype. When the installed
   * `ej2-popups` no longer exposes `getScalingFactor`, nothing is patched and a console
   * warning names the shim so the regression is visible instead of silent.
   * @returns Nothing.
   */
  install(): void {
    if (SyncfusionTooltipZoomService.installed) {
      return;
    }
    SyncfusionTooltipZoomService.installed = this.patchScalingFactor();
  }

  /**
   * Reads the effective CSS zoom currently applied to `<body>`.
   * @returns The zoom factor, `1` when none is applied.
   */
  private getBodyZoom(): number {
    const win = this.document.defaultView!;
    return parseFloat(win.getComputedStyle(this.document.body).zoom as string) || 1;
  }

  /**
   * Wraps `Tooltip.prototype.getScalingFactor` so a body-hosted popup under a body zoom
   * reports that zoom as its scale (see the class JSDoc for the coordinate-space reasoning).
   * @returns True when the prototype was patched; false (with a console warning) when the
   * installed Syncfusion build does not expose the private method the shim relies on.
   */
  private patchScalingFactor(): boolean {
    const getZoom = () => this.getBodyZoom();
    const win = this.document.defaultView!;
    // getScalingFactor is private in Syncfusion's typings; the cast is required to wrap it.
    const proto = Tooltip.prototype as unknown as Partial<TooltipInternals>;
    const originalGetScalingFactor = proto.getScalingFactor;
    if (typeof originalGetScalingFactor !== 'function') {
      console.warn(
        '[SyncfusionTooltipZoomService] Tooltip.prototype.getScalingFactor is missing in this ' +
        '@syncfusion/ej2-popups build; the tooltip body-zoom shim was not installed.',
      );
      return false;
    }
    proto.getScalingFactor = function (this: TooltipInternals, target: HTMLElement | null | undefined): TooltipScalingFactors {
      const factors = originalGetScalingFactor.call(this, target);
      if (!target) {
        return factors;
      }
      const zoom = getZoom();
      if (zoom === 1 || !this.isBodyContainer || !this.tooltipEle) {
        return factors;
      }
      if (win.getComputedStyle(this.tooltipEle).position === 'fixed') {
        return factors;
      }
      // The popup is absolutely positioned inside the zoomed <body>, so every root-pixel
      // measurement Syncfusion takes is `zoom` times the CSS pixels it will position in.
      return { x: factors.x * zoom, y: factors.y * zoom };
    };
    return true;
  }
}
